import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GifPickerPill, RecognitionDraftCard, RecognitionDraftVariant } from '@bonusly/bizy-kit';
import { Box, Divider, HStack, IconButton, Image, Menu, MenuButton, MenuItem, MenuList, Text } from '@bonusly/design-system';

import { DotsHorizontalOutline, TrashOutline } from '../../../design-system/icon';
import type { SocialAttachment } from '../../action-input/lib/entities';
import { PillTab } from '../../tabs/pill-tab';
import { useBizyUiBlockActionDispatcher } from '../lib/ui-block-action-dispatcher';
import { GifPicker } from './gif-picker-card';
import { RECOGNITION_DRAFT_FOCUS_TAB_PROPS } from './lib/recognition-draft-focus-tab-props';
import { AmountPill, HashtagToken, RecipientHandle } from './lib/recognition-segments';
import { UiBlockActionBar } from './ui-block-action-bar';

/**
 * Recognition draft card — the user is writing a recognition and
 * Bizy is offering a drafted message.
 *
 * Visual shape (matches the live feed's recognition rendering — see
 * `src/components/bonus/text-preview.tsx` for the source-of-truth
 * styling we're re-using):
 *
 *   Frederick Allen: [+10] @judith.bartels Thank you for ... #hashtag
 *
 *   [📋]                                   [Send now] [Edit in givebox]
 *
 * Each segment uses the same color treatment a real bonus uses, so the
 * draft reads visually like the post the user is about to commit. No
 * divider line — vertical spacing separates the body from the action
 * row (the action bar handles the `mt`).
 *
 * BIZY-424: when the payload carries multiple `variants` (each
 * foregrounding a different facet of the recognition) the card shows a
 * tab per focus; selecting one swaps the body + actions client-side (no
 * round-trip), so the user picks an angle instead of looping through
 * "make it warmer" tweaks. The shared giver/recipient header stays put.
 * With zero or one variant it renders exactly as it always has, reading
 * from the top-level payload fields.
 */
export function RecognitionDraftCardView({
	block,
	fontSize = 'sm',
	isReadOnly = false,
	gifPicker,
}: {
	block: RecognitionDraftCard;
	fontSize?: 'sm' | 'md';
	isReadOnly?: boolean;
	/**
	 * The sibling gif picker payload. When present, the picker is embedded in
	 * this card so it shares the active variant and dispatches the attach_gif
	 * turn; the document renderer routes it here and skips the standalone node.
	 */
	gifPicker?: { pills: GifPickerPill[]; primaryQuery?: string | null };
}) {
	const { t } = useTranslation('admin_bizy');
	const dispatch = useBizyUiBlockActionDispatcher();
	const { giver, recipient, amount, message, hashtags, actions, variants, attachment, coachingPrompt } = block.payload;
	const [activeIndex, setActiveIndex] = useState(0);
	// An attach_gif turn is in flight. We hide the picker and ignore further
	// selections until the server re-emits the card, so a burst of clicks can't
	// post duplicate attach turns for the same draft.
	const [attachPending, setAttachPending] = useState(false);
	// Clear the guard once the server re-emits the card with a new attachment
	// state (gif added or removed). Without this, a re-render in place would
	// leave the guard stuck — blocking a later remove and keeping the picker
	// hidden after the gif is gone. Render-time reset (no effect) per the
	// React "adjust state when a prop changes" pattern.
	const attachmentKey = attachment?.gif ?? null;
	const [pendingForAttachment, setPendingForAttachment] = useState(attachmentKey);
	if (pendingForAttachment !== attachmentKey) {
		setPendingForAttachment(attachmentKey);
		setAttachPending(false);
	}

	// The top-level fields already mirror the first variant, so a card
	// with one (or zero) variant renders identically to the pre-BIZY-424
	// shape. With several, the selected tab drives the body + actions
	// while giver/recipient stay shared.
	const hasTabs = (variants?.length ?? 0) > 1;
	const fallbackVariant: RecognitionDraftVariant = { focus: null, message, amount, hashtags, actions, qualityScore: null, coachingPrompt };
	// Clamp so a stale activeIndex (e.g. a streamed re-render that shortened
	// the list) can never desync the highlighted tab from the shown body.
	const safeIndex = Math.min(activeIndex, Math.max(0, (variants?.length ?? 1) - 1));
	const active = variants?.[safeIndex] ?? fallbackVariant;

	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const activeMessage = active.message;
	const activeAmount = active.amount;
	const activeActions = active.actions ?? [];
	const activeCoachingPrompt = hasTabs ? active.coachingPrompt : (active.coachingPrompt ?? coachingPrompt);
	const hashtagList = (active.hashtags ?? []).map((h) => h.replace(/^#/, ''));

	// Copy text reproduces what the user would paste into the give-box
	// input: amount + recipient mentions + body + hashtags. We hand
	// this to the action bar's left-side copy icon.
	const copyText = [
		activeAmount && activeAmount > 0 ? `+${activeAmount}` : null,
		...recipients.map((r) => (r.startsWith('@') ? r : `@${r}`)),
		activeMessage,
		...hashtagList.map((h) => `#${h}`),
	]
		.filter(Boolean)
		.join(' ');

	// Attaching/removing a gif posts an attach_gif turn carrying the full draft
	// (top-level fields + all focus variants) + the picker's pills, so Bizy
	// re-emits the same card — tabs intact — with the gif threaded in.
	const dispatchAttachGif = (nextAttachment: Record<string, unknown> | null) => {
		if (attachPending) return;
		setAttachPending(true);
		dispatch({
			type: 'attach_gif',
			payload: {
				recipient,
				amount: amount ?? null,
				message,
				hashtags: hashtags ?? [],
				actions: actions ?? [],
				...(variants ? { variants } : {}),
				...(coachingPrompt ? { coachingPrompt } : {}),
				pills: gifPicker?.pills ?? [],
				...(gifPicker?.primaryQuery ? { primaryQuery: gifPicker.primaryQuery } : {}),
				attachment: nextAttachment,
			},
		});
	};
	const handleSelectGif = (social: SocialAttachment) => dispatchAttachGif(social.toJSON() as unknown as Record<string, unknown>);
	const handleRemoveGif = () => dispatchAttachGif(null);

	return (
		<Box my={2}>
			<Box px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
				{hasTabs && (
					<>
						{/* BIZY-453: the focus tabs stay on a single horizontal row, but when the panel is too narrow to fit them the row scrolls horizontally (rather than clipping a pill mid-word). `flexShrink={0}` keeps each pill at full width so its label stays whole, and the native scrollbar is hidden to match the Bizy prompt-chips rail (see `src/components/prompt-chips/prompt-chips.tsx`). */}
						{/* `fontSize` + `lineHeight` on this wrapper keep tabs at sm/20px even when the parent passes `fontSize="md"` on fullscreen desktop. */}
						<Box overflowX="auto" mb={3} fontSize="sm" lineHeight="5" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
							<HStack role="tablist" spacing={2}>
								{variants?.map((v, i) => (
									<PillTab
										key={`${v.focus ?? 'focus'}-${i}`}
										role="tab"
										aria-selected={i === safeIndex}
										isActive={i === safeIndex}
										flexShrink={0}
										{...RECOGNITION_DRAFT_FOCUS_TAB_PROPS}
										onClick={() => setActiveIndex(i)}
									>
										{v.focus || t('uiBlocks.recognitionDraft.focusFallback', { index: i + 1 })}
									</PillTab>
								))}
							</HStack>
						</Box>
						{/* Full-width rule between the focus tabs and the draft body (matches the Figma). It is a sibling of the scroll Box above (never a child of it), so the scrollable pill row can't constrain it. BIZY-453: `width="auto"` overrides Chakra Divider's default `width:100%`, which—once the overflowing pill row stretches the card's layout width—resolves against the narrower visible width and leaves a gap on the right. With `auto`, the symmetric negative `mx={-3}` (cancelling the card's `px={3}`) drives both edges flush to the card border. */}
						<Divider borderColor="border.default" width="auto" mx={-3} mb={3} />
					</>
				)}
				{/* `fontSize="inherit"` on every segment + Text keeps the giver header, +amount pill, @recipient, body, and hashtags at the same visual size. */}
				<Text fontSize={fontSize} lineHeight="tall" whiteSpace="pre-wrap">
					{giver && (
						<Text as="span" fontSize="inherit" fontWeight="semibold">
							{giver}:{' '}
						</Text>
					)}
					{activeAmount != null && activeAmount > 0 && (
						<>
							<AmountPill amount={activeAmount} />{' '}
						</>
					)}
					{recipients.map((r, i) => (
						<span key={`${r}-${i}`}>
							<RecipientHandle username={r} />{' '}
						</span>
					))}
					{activeMessage}
					{hashtagList.length > 0 && (
						<>
							{' '}
							{hashtagList.map((h, i) => (
								<span key={`${h}-${i}`}>
									<HashtagToken hashtag={h} />
									{i < hashtagList.length - 1 ? ' ' : ''}
								</span>
							))}
						</>
					)}
				</Text>
				{attachment?.gif && (
					// Centered, contained, rounded — matches the feed's gif treatment
					// (see recognition/bonus/.../image/image-list.tsx).
					<Box mt={2} position="relative" role="group" w="fit-content" maxW="100%" mx="auto">
						<Box borderRadius="lg" overflow="hidden">
							<Image src={attachment.gif} alt={attachment.altText ?? ''} display="block" maxH="220px" maxW="100%" objectFit="contain" w="auto" />
						</Box>
						{!isReadOnly && (
							<Menu>
								<MenuButton
									as={IconButton}
									icon={<DotsHorizontalOutline size="sm" />}
									aria-label={t('uiBlocks.gifPicker.options')}
									position="absolute"
									top={1}
									right={1}
									size="xs"
									isRound
									variant="solid"
									bg="blackAlpha.600"
									color="white"
									_hover={{ bg: 'blackAlpha.700' }}
								/>
								<MenuList minW="150px" p={1} border="none">
									<MenuItem onClick={handleRemoveGif} borderRadius="md" color="error.fg">
										<HStack spacing={2}>
											<TrashOutline size="sm" />
											<Text>{t('uiBlocks.gifPicker.remove')}</Text>
										</HStack>
									</MenuItem>
								</MenuList>
							</Menu>
						)}
					</Box>
				)}
				<UiBlockActionBar actions={activeActions} copyText={copyText} areActionsDisabled={isReadOnly} />
			</Box>
			{activeCoachingPrompt && (
				<Text mt={2} fontSize="sm" color="text.softer" lineHeight="short">
					{activeCoachingPrompt}
				</Text>
			)}
			{/* A separate block below the draft card, with a short line between them.
			    Only offered while the draft has no gif yet — once one is attached it
			    lives on the card (with its Remove kebab); removing it brings it back. */}
			{gifPicker && !isReadOnly && !attachment?.gif && !attachPending && (
				<Box mb={2}>
					<Text fontSize={fontSize} color="text.soft" mb={1}>
						{t('uiBlocks.gifPicker.intro')}
					</Text>
					<GifPicker pills={gifPicker.pills} primaryQuery={gifPicker.primaryQuery} onSelectGif={handleSelectGif} />
				</Box>
			)}
		</Box>
	);
}
